import React from "react";

interface Props {
  children: React.ReactNode;
  // "page" (the app-root default) fills the viewport - appropriate when the
  // whole app has nothing else to show. "section" is a compact inline card,
  // sized to sit inside one dashboard tab so a crash in, say, a Peer
  // Benchmark chart doesn't take Overview/Filters/every other tab down with
  // it - Radix Tabs unmounts inactive panels, so switching away and back
  // remounts this boundary fresh without any extra reset logic needed.
  variant?: "page" | "section";
  testId?: string;
}

interface State {
  hasError: boolean;
}

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      if (this.props.variant === "section") {
        return (
          <div className="card-premium p-6 text-center sm:p-8" data-testid={this.props.testId || "section-error-boundary"}>
            <h3 className="text-base font-extrabold text-[hsl(var(--ink))]">This section took a tea break</h3>
            <p className="mx-auto mt-1.5 max-w-md text-sm leading-relaxed text-[hsl(var(--muted-ink))]">
              The data bees hit an unexpected snag here - the rest of your analysis is unaffected. Switch to another tab and back, or reload, to try again.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="btn-sheen mt-4 rounded-lg bg-[hsl(var(--honey))] px-5 py-2.5 text-sm font-extrabold text-[hsl(var(--ink))]"
              data-testid="section-error-boundary-reload"
            >
              Reload the app
            </button>
          </div>
        );
      }
      return (
        <div className="flex min-h-screen items-center justify-center px-4">
          <div className="card-premium max-w-md p-8 text-center">
            <h2 className="text-xl font-extrabold text-[hsl(var(--ink))]">This section took a tea break</h2>
            <p className="mt-2 text-sm text-[hsl(var(--muted-ink))]">
              The data bees hit an unexpected snag, but your analysis is safe. Refresh the page to continue.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="btn-sheen mt-5 rounded-lg bg-[hsl(var(--honey))] px-5 py-2.5 text-sm font-extrabold text-[hsl(var(--ink))]"
              data-testid="error-boundary-reload"
            >
              Reload the app
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
